import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  ErrorState,
  Input,
  Table,
} from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type {
  Subject,
  SubjectListItem,
} from '../../domain/entities/content.entity';
import { useSubjects, useDeleteSubject } from '../hooks/use-subjects';
import { useCanWriteSubjects } from '../hooks/use-content-permissions';
import { Mono, formatDateTime, formatPercentage } from '../display';
import { Pagination } from './Pagination';
import { SubjectFormModal } from './SubjectFormModal';
import { ConfirmModal } from './ConfirmModal';
import { RefreshButton } from '@/shared/RefreshButton';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 10;
const SEARCH_PARAM = 'search';
const INCLUDE_DELETED_PARAM = 'includeDeleted';

export function SubjectsPanel() {
  // Página, busca aplicada e "incluir excluídos" vivem na URL (não em
  // useState) — ver primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md. Aplicar
  // busca e alternar o checkbox resetam a página no mesmo handler, então
  // cada mudança vai num único setSearchParams (ver aviso em
  // use-page-query-param.ts). `includeDeleted` não usa `useUrlFilters` por
  // ser booleano, não string.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = readPageParam(searchParams);
  const search = searchParams.get(SEARCH_PARAM) ?? '';
  const includeDeleted = searchParams.get(INCLUDE_DELETED_PARAM) === 'true';
  const [searchInput, setSearchInput] = useState(search);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [renaming, setRenaming] = useState<Subject | null>(null);
  const [deleting, setDeleting] = useState<Subject | null>(null);

  const { data, isLoading, isError, isSuccess, refetch, isFetching } =
    useSubjects({
      page,
      perPage: PER_PAGE,
      search,
      includeDeleted,
    });
  const deleteSubject = useDeleteSubject();
  const canWrite = useCanWriteSubjects();

  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (trimmed) params.set(SEARCH_PARAM, trimmed);
        else params.delete(SEARCH_PARAM);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const toggleIncludeDeleted = (checked: boolean) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (checked) params.set(INCLUDE_DELETED_PARAM, 'true');
        else params.delete(INCLUDE_DELETED_PARAM);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deleteSubject.mutate(deleting.id, {
      onSuccess: () => {
        setDeleting(null);
        deleteSubject.reset();
      },
    });
  };

  const columns: ColumnDef<SubjectListItem>[] = [
    {
      key: 'name',
      header: 'Nome',
      render: (row) => (
        <span className="text-(--color-text-primary) font-medium">
          {row.name}
        </span>
      ),
    },
    { key: 'id', header: 'ID', render: (row) => <Mono>{row.id}</Mono> },
    {
      key: 'status',
      header: 'Situação',
      render: (row) =>
        row.deletedAt ? (
          <Badge variant="warning">Excluído</Badge>
        ) : (
          <Badge variant="success">Ativo</Badge>
        ),
    },
    {
      // Só leitura: o número sai do rebuild, e o admin não o recalcula.
      key: 'generalIncidence',
      header: 'Incidência geral',
      render: (row) => (
        <span className="tabular-nums">
          {formatPercentage(row.generalIncidence)}
        </span>
      ),
    },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: '',
            render: (row: SubjectListItem) =>
              row.deletedAt ? null : (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenaming(row);
                    }}
                  >
                    Renomear
                  </Button>
                  <Button
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleting(row);
                    }}
                  >
                    Excluir
                  </Button>
                </div>
              ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <form onSubmit={applySearch} className="flex items-end gap-2">
          <Input
            label="Buscar"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Nome do assunto…"
          />
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
        </form>
        <div className="flex shrink-0 items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          {canWrite && (
            <Button onClick={() => setIsCreateOpen(true)}>Criar assunto</Button>
          )}
        </div>
      </div>

      <label className="text-(--color-text-secondary) flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={includeDeleted}
          onChange={(e) => toggleIncludeDeleted(e.target.checked)}
        />
        Incluir excluídos
      </label>

      {isError ? (
        // Mensagem fixa: a frase crua da API não diz nada a quem administra.
        <ErrorState
          message="Não foi possível carregar os assuntos."
          onRetry={() => void refetch()}
        />
      ) : (
        <>
          {isSuccess && (
            <p className="text-(--color-text-secondary) text-sm">
              {data.incidenceRebuiltAt
                ? `Incidência calculada em ${formatDateTime(data.incidenceRebuiltAt)}`
                : 'Incidência ainda não calculada'}
            </p>
          )}

          <Table
            columns={columns}
            data={data?.items ?? []}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage="Nenhum assunto encontrado."
            striped
          />

          <Pagination
            page={page}
            total={data?.total ?? 0}
            perPage={PER_PAGE}
            loading={isLoading}
            label="assunto"
            onPageChange={setPage}
          />
        </>
      )}

      <SubjectFormModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
      <SubjectFormModal
        open={!!renaming}
        subject={renaming}
        onClose={() => setRenaming(null)}
      />
      <ConfirmModal
        open={!!deleting}
        onClose={() => {
          setDeleting(null);
          deleteSubject.reset();
        }}
        onConfirm={confirmDelete}
        title="Excluir assunto"
        description={`Excluir "${deleting?.name}"? Ele deixa de aparecer nas listagens (exclusão lógica).`}
        confirmLabel="Excluir"
        loading={deleteSubject.isPending}
        error={deleteSubject.error}
      />
    </div>
  );
}
